/**
 * Sort options for the Inbox conversation list. Pure logic (no React) so it
 * can be unit tested; the Inbox renders these in a compact select next to the
 * conversation search box. Null timestamps/subjects always sort last so
 * conversations without messages don't jump to the top.
 */

import { compareSortValues } from './listFilterSort'
import type { SortOption } from './listFilterSort'

export type InboxSortKey = 'newest' | 'oldest' | 'unread' | 'name' | 'subject'

export const INBOX_SORT_OPTIONS: SortOption[] = [
  { value: 'newest', label: 'Newest activity' },
  { value: 'oldest', label: 'Oldest activity' },
  { value: 'unread', label: 'Unread first' },
  { value: 'name', label: 'Name A\u2013Z' },
  { value: 'subject', label: 'Subject A\u2013Z' },
]

export const DEFAULT_INBOX_SORT: InboxSortKey = 'newest'

export function isInboxSortKey(value: string): value is InboxSortKey {
  return INBOX_SORT_OPTIONS.some((o) => o.value === value)
}

export interface SortableConversation {
  contact_name?: string | null
  contact_info?: string | null
  subject?: string | null
  unread_count?: number
  last_message_at?: string | null
}

/** ISO timestamps compare lexicographically; nulls always last. */
function compareActivity(a: string | null | undefined, b: string | null | undefined, dir: 'asc' | 'desc'): number {
  if (!a && !b) return 0
  if (!a) return 1
  if (!b) return -1
  return dir === 'asc' ? a.localeCompare(b) : b.localeCompare(a)
}

export function sortConversations<T extends SortableConversation>(
  conversations: T[],
  sortBy: InboxSortKey
): T[] {
  const sorted = [...conversations]
  switch (sortBy) {
    case 'oldest':
      sorted.sort((a, b) => compareActivity(a.last_message_at, b.last_message_at, 'asc'))
      break
    case 'unread':
      sorted.sort((a, b) => {
        const aUnread = (a.unread_count || 0) > 0 ? 0 : 1
        const bUnread = (b.unread_count || 0) > 0 ? 0 : 1
        if (aUnread !== bUnread) return aUnread - bUnread
        return compareActivity(a.last_message_at, b.last_message_at, 'desc')
      })
      break
    case 'name':
      sorted.sort((a, b) =>
        compareSortValues(a.contact_name || a.contact_info || null, b.contact_name || b.contact_info || null)
      )
      break
    case 'subject':
      sorted.sort((a, b) => {
        const cmp = compareSortValues(a.subject || null, b.subject || null)
        if (cmp !== 0) return cmp
        return compareActivity(a.last_message_at, b.last_message_at, 'desc')
      })
      break
    case 'newest':
    default:
      sorted.sort((a, b) => compareActivity(a.last_message_at, b.last_message_at, 'desc'))
      break
  }
  return sorted
}
